/**
 * useShortcut
 *
 * Thin React adapter over `shortcut-manager`'s `registerShortcut`.
 * Pass a catalog entry plus a handler:
 *
 *     useShortcut(SHORTCUTS.omnibarFocusAdd, () => {
 *       app.setOmnibarMode("add");
 *       focusOmnibarInput();
 *     });
 *
 * The `handler` argument is captured fresh on every render via a ref, so
 * callers don't need to memoize the callback or worry about stale
 * closures. Effect deps are kept narrow (the spec id) so a handler change
 * doesn't churn the manager registration.
 *
 * `enabled` is also captured fresh and re-evaluated on every keystroke
 * by the manager, so consumers can return live state without re-wiring.
 */
import { useEffect, useRef } from "react";
import { registerShortcut, } from "./shortcut-manager";
export function useShortcut(spec, handler, options) {
    // Refs let the registration close over the latest handler / enabled
    // predicate without re-registering when those change. Re-registering
    // on every render would clear and rebuild the manager's entry on each
    // commit — wasteful and racy if it happens between a keydown and the
    // dispatch loop reading the registrations array.
    const handlerRef = useRef(handler);
    handlerRef.current = handler;
    const enabledRef = useRef(options?.enabled);
    enabledRef.current = options?.enabled;
    useEffect(() => {
        return registerShortcut({
            spec,
            handler: (e) => handlerRef.current(e),
            enabled: () => {
                const fn = enabledRef.current;
                return fn === undefined ? true : fn();
            },
        });
        // The catalog entry is stable across renders (it's a const literal),
        // so `spec.id` is the natural key — re-register only if a caller
        // genuinely swaps which shortcut a hook is bound to.
    }, [spec]);
}
